import { useMemo } from "react";
import { History } from "lucide-react";
import CHANGELOG from "../../../../CHANGELOG.md?raw";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/misc";
import { Page, PageHeader } from "@/components/ui/page";
import { Markdown, NO_HANDLES } from "@/components/markdown";
import { APP_COMMIT, APP_VERSION } from "@/lib/app-version";
import { parseChangelog } from "@/lib/changelog";

/**
 * What changed in each release, newest first: the repo's CHANGELOG.md, built into
 * the bundle, so the page always describes the version that is drawing it.
 */
export function ChangelogView({ showTitle = true }: { showTitle?: boolean }) {
  const releases = useMemo(() => parseChangelog(CHANGELOG), []);
  return (
    <Page>
      <PageHeader title="Changelog" showTitle={showTitle} description="What changed in each release, newest first." />
      <p className="mb-4 text-[13.5px] text-muted-foreground">
        You're on <span className="font-semibold text-foreground">{APP_VERSION}</span>
        {APP_COMMIT ? (
          <>
            , deployed <code className="font-mono text-[12.5px] text-foreground">{APP_COMMIT}</code>
          </>
        ) : null}
        .
      </p>
      {releases.length === 0 ? (
        <EmptyState icon={<History />} title="No releases yet" hint="CHANGELOG.md has no version entries." />
      ) : (
        <div className="space-y-3">
          {releases.map((release) => (
            <Card key={release.version} className="p-4 win-sm:p-5">
              <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="text-base font-semibold tracking-tight">{release.version}</h2>
                {release.date ? <span className="text-[12.5px] text-muted-foreground">{release.date}</span> : null}
                {release.version === APP_VERSION ? (
                  <Badge variant="tinted" tint="var(--kind-done)">
                    current
                  </Badge>
                ) : null}
              </div>
              {release.body ? <Markdown text={release.body} handles={NO_HANDLES} className="text-[13.5px]" /> : null}
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}
